// ============================================
// PAGE - Etiquetas Granel
// ============================================

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductosGranel, type ProductoGranel } from '@almacenes/core';
import { MdArrowBack, MdPrint, MdSearch, MdSelectAll } from 'react-icons/md';
import Barcode from 'react-barcode';

export const EtiquetasGranelPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const printRef = useRef<HTMLDivElement>(null);
  const [productosImprimir, setProductosImprimir] = useState<ProductoGranel[]>([]);

  const { data: productos = [], isLoading } = useProductosGranel();

  // Filtrar productos según búsqueda
  const productosFiltrados = productos.filter((p) =>
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigoBarra.includes(searchTerm) ||
    p.categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.marca.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Manejar selección individual
  const handleToggleSelect = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Seleccionar todos
  const handleSelectAll = () => {
    if (selectedIds.size === productosFiltrados.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(productosFiltrados.map((p) => p.productoAlmacenId)));
    }
  };

  // Obtener productos para imprimir
  const getProductosParaImprimir = (mode: 'all' | 'selected' | number) => {
    if (mode === 'all') {
      return productosFiltrados;
    } else if (mode === 'selected') {
      return productosFiltrados.filter((p) => selectedIds.has(p.productoAlmacenId));
    } else {
      return productosFiltrados.filter((p) => p.productoAlmacenId === mode);
    }
  };

  // Función de impresión mejorada
  const handleImprimirClick = (mode: 'all' | 'selected' | number) => {
    const productosAImprimir = getProductosParaImprimir(mode);
    if (productosAImprimir.length === 0) {
      alert('No hay productos para imprimir');
      return;
    }
    
    setProductosImprimir(productosAImprimir);
    
    // Esperar a que React actualice el DOM
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Etiquetas Granel</title>
                <style>
                  @page {
                    size: A4;
                    margin: 10mm;
                  }
                  * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                  }
                  body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    font-family: Arial, sans-serif;
                  }
                  .etiquetas-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 5mm;
                    width: 100%;
                  }
                  .etiqueta {
                    width: 100%;
                    min-height: 45mm;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    padding: 3mm;
                    border: 1px dashed #ccc;
                    position: relative;
                    background: white;
                    break-inside: avoid;
                  }
                  .badge {
                    position: absolute;
                    top: 2mm;
                    left: 2mm;
                    background-color: #E0F2FE;
                    color: #0369A1;
                    padding: 1px 6px;
                    border-radius: 4px;
                    font-size: 8px;
                    font-weight: 600;
                  }
                  .nombre {
                    font-size: 12px;
                    font-weight: bold;
                    text-align: center;
                    margin-bottom: 2mm;
                    max-width: 85mm;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  }
                  .metadata {
                    font-size: 9px;
                    color: #6B7280;
                    text-align: center;
                    margin-bottom: 2mm;
                  }
                  .barcode-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  }
                  @media print {
                    .etiquetas-grid {
                      page-break-inside: avoid;
                    }
                  }
                </style>
              </head>
              <body>
                <div class="etiquetas-grid">
                  ${printRef.current.innerHTML}
                </div>
              </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.focus();
          
          // Pequeño delay para asegurar que todo esté cargado
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 250);
        }
      }
    }, 100);
  };

  return (
    <div className="min-h-screen">
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/inventario')}
              className="p-2 rounded-lg transition-all hover:bg-gray-200"
              style={{ color: '#11194F' }}
            >
              <MdArrowBack className="text-2xl" />
            </button>
            <h1 className="text-4xl font-bold" style={{ color: '#11194F' }}>
              Etiquetas Productos Granel
            </h1>
          </div>

          {/* Botones de acción principales */}
          <div className="flex gap-3">
            <button
              onClick={() => handleImprimirClick('selected')}
              disabled={selectedIds.size === 0}
              className="px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
              style={{ backgroundColor: '#43BFFF' }}
            >
              <MdPrint className="text-xl" />
              <span>Imprimir Seleccionados ({selectedIds.size})</span>
            </button>

            <button
              onClick={() => handleImprimirClick('all')}
              disabled={productosFiltrados.length === 0}
              className="px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
              style={{ backgroundColor: '#FACC15', color: '#11194F' }}
            >
              <MdPrint className="text-xl" />
              <span>Imprimir Todos ({productosFiltrados.length})</span>
            </button>
          </div>
        </div>

        {/* Barra de búsqueda y seleccionar todos */}
        <div className="mb-6 flex gap-3">
          <div className="flex-1 relative">
            <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, código, categoría, tipo o marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:border-primary transition-colors"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>

          <button
            onClick={handleSelectAll}
            className="px-6 py-4 rounded-xl font-semibold transition-all hover:bg-gray-200 border-2 border-gray-300 flex items-center gap-2"
            style={{ color: '#11194F' }}
          >
            <MdSelectAll className="text-2xl" />
            <span>{selectedIds.size === productosFiltrados.length ? 'Deseleccionar' : 'Seleccionar'} Todos</span>
          </button>
        </div>

        {/* Lista de productos */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
            </div>
          ) : productosFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No se encontraron productos granel</p>
              {searchTerm && (
                <p className="text-gray-500 mt-2">Intenta con otro término de búsqueda</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {productosFiltrados.map((producto) => (
                <div
                  key={producto.productoAlmacenId}
                  onClick={() => handleToggleSelect(producto.productoAlmacenId)}
                  className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedIds.has(producto.productoAlmacenId)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  {/* Checkbox */}
                  <div className="absolute top-3 right-3 z-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(producto.productoAlmacenId)}
                      onChange={() => handleToggleSelect(producto.productoAlmacenId)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 cursor-pointer"
                    />
                  </div>

                  {/* Vista previa de la etiqueta */}
                  <div className="flex flex-col items-center">
                    {producto.marca && producto.marca.toLowerCase() !== 'sin marca' && (
                      <div className="text-xs font-semibold px-2 py-1 rounded bg-sky-100 text-sky-700 mb-2 self-start">
                        Con marca
                      </div>
                    )}
                    
                    <div className="text-sm font-bold mb-2 text-center w-full truncate px-6" style={{ color: '#11194F' }} title={producto.nombre}>
                      {producto.nombre}
                    </div>
                    
                    <div className="text-xs text-gray-600 mb-2 text-center">
                      {producto.categoria} - {producto.tipo}{producto.marca && producto.marca.toLowerCase() !== 'sin marca' ? ` - ${producto.marca}` : ''}
                    </div>
                    
                    <div className="flex justify-center mb-2">
                      <Barcode
                        value={producto.codigoBarra}
                        format="EAN13"
                        width={1.2}
                        height={35}
                        fontSize={9}
                        displayValue={true}
                      />
                    </div>

                    {/* Botón de impresión individual */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImprimirClick(producto.productoAlmacenId);
                      }}
                      className="mt-2 w-full px-3 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center justify-center gap-2 text-sm"
                      style={{ backgroundColor: '#43BFFF' }}
                    >
                      <MdPrint className="text-base" />
                      <span>Imprimir</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info total */}
        {!isLoading && productosFiltrados.length > 0 && (
          <div className="mt-4 text-center text-sm text-gray-600">
            Mostrando {productosFiltrados.length} producto{productosFiltrados.length !== 1 ? 's' : ''} granel
            {searchTerm && ` que coinciden con "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Área de impresión (oculta) */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {productosImprimir.map((producto, index) => {
            const tieneMarca = producto.marca && producto.marca.toLowerCase() !== 'sin marca';
            return (
              <div
                key={`print-${producto.productoAlmacenId}-${index}`}
                className="etiqueta"
              >                
                <div className="nombre">
                  {producto.nombre}
                </div>
                
                <div className="metadata" style={{ marginBottom: '3mm' }}>
                  {producto.categoria} - {producto.tipo}{tieneMarca ? ` - ${producto.marca}` : ''}
                </div>
                
                <div className="barcode-container">
                  <Barcode
                    value={producto.codigoBarra}
                    format="EAN13"
                    width={1.5}
                    height={40}
                    fontSize={11}
                    displayValue={true}
                    background="#FFFFFF"
                    lineColor="#000000"
                    margin={0}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
